/**
 * Client side of the in-place rebuild feature: talks to the collector's
 * /update/* endpoints (see feedback/update-endpoints.mjs) so the desktop-served
 * webapp can pull the latest pushed code and rebuild itself, all from Edd's
 * phone over the same Tailscale /collector path the feedback tool already uses.
 *
 * This is WEB-ONLY by design. The packaged Android APK has no local PC server to
 * talk to (it updates via the APK updater instead), so callers gate on
 * Capacitor.isNativePlatform() before ever using any of this.
 *
 * Every request is quiet: a dead or missing collector resolves to null rather
 * than throwing, so a phone with the PC switched off simply shows no update
 * control instead of spraying errors.
 */
import { FEEDBACK_COLLECTOR_URL } from './collectorConfig';

/** Where a rebuild is in its life. Mirrors the server's phase field. */
export type UpdatePhase = 'idle' | 'fetching' | 'pulling' | 'building' | 'done' | 'error';

/** The last build's outcome, as recorded by the collector. */
export interface LastBuild {
  ok: boolean;
  at: number;
  error?: string;
}

/** Snapshot from GET /update/status: are we behind origin, and by how much. */
export interface UpdateStatus {
  updateAvailable: boolean;
  behind: number;
  localSha: string | null;
  remoteSha: string | null;
  currentBranch: string | null;
  building: boolean;
  lastBuild: LastBuild | null;
  error?: string;
}

/** Snapshot from GET /update/progress while a rebuild runs. */
export interface UpdateProgress {
  building: boolean;
  phase: UpdatePhase;
  tail: string[];
  lastBuild: LastBuild | null;
}

/** Result of POST /update/run. */
export interface RunResult {
  started: boolean;
  reason?: string;
}

/** The collector base with any trailing slashes trimmed ('' when unset). */
export function updateBase(collectorUrl: string = FEEDBACK_COLLECTOR_URL): string {
  return collectorUrl.trim().replace(/\/+$/, '');
}

/**
 * Whether the update control can work at all: only when a collector base URL is
 * configured. With no base there's nothing but the same-origin vite server,
 * which has no /update endpoints, so we show nothing.
 */
export function updateConfigured(collectorUrl: string = FEEDBACK_COLLECTOR_URL): boolean {
  return updateBase(collectorUrl).length > 0;
}

/** Build a full /update/<path> URL, or '' when no collector base is configured. */
export function updateEndpoint(path: 'status' | 'run' | 'progress', collectorUrl: string = FEEDBACK_COLLECTOR_URL): string {
  const base = updateBase(collectorUrl);
  return base ? `${base}/update/${path}` : '';
}

function authHeaders(token?: string): Record<string, string> {
  return token ? { 'x-admin-token': token } : {};
}

/** Fetch with a timeout that resolves to null on any failure (never throws). */
async function fetchJson<T>(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<T | null> {
  if (!url) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...init, signal: ctrl.signal });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** GET /update/status. null when the collector is unreachable. */
export function fetchUpdateStatus(
  collectorUrl?: string,
  token?: string,
  timeoutMs = 8_000,
): Promise<UpdateStatus | null> {
  return fetchJson<UpdateStatus>(
    updateEndpoint('status', collectorUrl),
    { method: 'GET', headers: authHeaders(token) },
    timeoutMs,
  );
}

/** POST /update/run to kick a rebuild. null when the collector is unreachable. */
export function postUpdateRun(
  collectorUrl?: string,
  token?: string,
  timeoutMs = 12_000,
): Promise<RunResult | null> {
  return fetchJson<RunResult>(
    updateEndpoint('run', collectorUrl),
    { method: 'POST', headers: authHeaders(token) },
    timeoutMs,
  );
}

/** GET /update/progress while a rebuild runs. null when unreachable. */
export function fetchUpdateProgress(
  collectorUrl?: string,
  token?: string,
  timeoutMs = 8_000,
): Promise<UpdateProgress | null> {
  return fetchJson<UpdateProgress>(
    updateEndpoint('progress', collectorUrl),
    { method: 'GET', headers: authHeaders(token) },
    timeoutMs,
  );
}
